import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Plus, Trash2, Bell, ListTodo, Send, Repeat, Clock } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";

const PRIO_TONE = {
  low: "border-slate-300 text-slate-600 bg-slate-50",
  medium: "border-orange-300 text-orange-700 bg-orange-50",
  high: "border-rose-300 text-rose-700 bg-rose-50",
};

function nowLocalDateTime() {
  const d = new Date(); d.setSeconds(0, 0);
  d.setMinutes(d.getMinutes() + 60);
  const off = d.getTimezoneOffset();
  const local = new Date(d.getTime() - off * 60000);
  return local.toISOString().slice(0, 16);
}
function toIsoUtc(localDt) { if (!localDt) return null; return new Date(localDt).toISOString(); }
function fromIsoLocal(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const off = d.getTimezoneOffset();
  const local = new Date(d.getTime() - off * 60000);
  return local.toISOString().slice(0, 16);
}

function emptyForm() {
  return {
    title: "", description: "", due_at: nowLocalDateTime(), reminder_at: "",
    priority: "medium", status: "open", repeat: "none", assigned_to: "none",
  };
}

export default function Tasks() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [users, setUsers] = useState([]);
  const [statusFilter, setStatusFilter] = useState("open");
  const [mine, setMine] = useState(false);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [editing, setEditing] = useState(null);

  const load = () => {
    const params = {};
    if (statusFilter !== "all") params.status = statusFilter;
    if (mine) params.mine = true;
    api.get("/tasks", { params }).then(({ data }) => setRows(data));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [statusFilter, mine]);
  useEffect(() => { api.get("/users").then(({ data }) => setUsers(data)).catch(() => setUsers([])); }, []);

  const submit = async () => {
    if (!form.title.trim()) { toast.error("Enter a title"); return; }
    const payload = {
      title: form.title, description: form.description || null,
      due_at: toIsoUtc(form.due_at),
      reminder_at: form.reminder_at ? toIsoUtc(form.reminder_at) : null,
      priority: form.priority, status: form.status,
      repeat: form.repeat,
      assigned_to: form.assigned_to === "none" ? null : form.assigned_to,
    };
    try {
      if (editing) {
        await api.patch(`/tasks/${editing}`, payload);
        toast.success("Task updated");
      } else {
        await api.post("/tasks", payload);
        toast.success("Task created");
      }
      setOpen(false); setForm(emptyForm()); setEditing(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };

  const toggleDone = async (t) => {
    await api.patch(`/tasks/${t.task_id}`, { status: t.status === "done" ? "open" : "done" });
    load();
  };
  const del = async (id) => { if (!window.confirm("Delete this task?")) return; await api.delete(`/tasks/${id}`); load(); };
  const snooze = async (id, minutes) => { await api.post(`/tasks/${id}/snooze`, { minutes }); toast.success("Snoozed"); load(); };
  const sendTest = async () => {
    try { const { data } = await api.post("/tasks/test-reminder"); toast.success(`Test sent to ${data.sent_to}`); }
    catch (e) { toast.error(e?.response?.data?.detail || "Test send failed"); }
  };
  const edit = (t) => {
    setEditing(t.task_id);
    setForm({
      title: t.title, description: t.description || "",
      due_at: fromIsoLocal(t.due_at),
      reminder_at: t.reminder_at ? fromIsoLocal(t.reminder_at) : "",
      priority: t.priority || "medium", status: t.status || "open",
      repeat: t.repeat || "none",
      assigned_to: t.assigned_to || "none",
    });
    setOpen(true);
  };

  return (
    <div className="p-6 md:p-10 max-w-5xl mx-auto" data-testid="tasks-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
        <div>
          <div className="text-xs uppercase tracking-widest font-mono-plex text-slate-500">Personal</div>
          <h1 className="font-heading text-4xl md:text-5xl font-black flex items-center gap-2"><ListTodo className="w-9 h-9" /> Tasks</h1>
          <p className="text-slate-500 mt-1 text-sm">Independent to-dos with due times and optional reminders.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={sendTest} data-testid="task-test-reminder"><Send className="w-4 h-4 mr-1" /> Test reminder</Button>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-40" data-testid="tasks-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="open">Open</SelectItem>
              <SelectItem value="in_progress">In progress</SelectItem>
              <SelectItem value="done">Done</SelectItem>
              <SelectItem value="cancelled">Cancelled</SelectItem>
            </SelectContent>
          </Select>
          <div className="flex items-center gap-1 text-sm">
            <Checkbox checked={mine} onCheckedChange={setMine} data-testid="tasks-mine" />
            <Label className="cursor-pointer">Mine</Label>
          </div>
          <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setForm(emptyForm()); setEditing(null); } }}>
            <DialogTrigger asChild><Button data-testid="task-new-btn" className="bg-slate-900 hover:bg-slate-800"><Plus className="w-4 h-4 mr-1" /> New task</Button></DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader><DialogTitle>{editing ? "Edit task" : "New task"}</DialogTitle></DialogHeader>
              <div className="grid gap-3">
                <div><Label>Title</Label><Input data-testid="task-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></div>
                <div><Label>Description</Label><Textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
                <div className="grid grid-cols-2 gap-2">
                  <div><Label>Due (local)</Label><Input type="datetime-local" data-testid="task-due" value={form.due_at} onChange={(e) => setForm({ ...form, due_at: e.target.value })} /></div>
                  <div><Label>Reminder <span className="text-slate-400 text-[10px] font-mono-plex">(optional)</span></Label><Input type="datetime-local" data-testid="task-reminder" value={form.reminder_at} onChange={(e) => setForm({ ...form, reminder_at: e.target.value })} /></div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div><Label>Priority</Label>
                    <Select value={form.priority} onValueChange={v => setForm({ ...form, priority: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="low">Low</SelectItem><SelectItem value="medium">Medium</SelectItem><SelectItem value="high">High</SelectItem></SelectContent>
                    </Select>
                  </div>
                  <div><Label>Assign to</Label>
                    <Select value={form.assigned_to} onValueChange={v => setForm({ ...form, assigned_to: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Just for me</SelectItem>
                        {users.map(u => <SelectItem key={u.user_id} value={u.user_id}>{u.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div><Label>Repeat</Label>
                  <Select value={form.repeat} onValueChange={v => setForm({ ...form, repeat: v })}>
                    <SelectTrigger data-testid="task-repeat"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Doesn't repeat</SelectItem>
                      <SelectItem value="daily">Daily</SelectItem>
                      <SelectItem value="weekly">Weekly</SelectItem>
                      <SelectItem value="monthly">Monthly</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                <Button onClick={submit} data-testid="task-save" className="bg-slate-900 hover:bg-slate-800">{editing ? "Save" : "Create"}</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="space-y-3" data-testid="tasks-list">
        {rows.length === 0 && <Card className="p-8 text-center text-slate-400 border-dashed border-slate-300 shadow-none">No tasks match.</Card>}
        {rows.map((t) => {
          const overdue = t.status !== "done" && new Date(t.due_at) < new Date();
          const remindDue = t.reminder_at && new Date(t.reminder_at) <= new Date() && t.status !== "done";
          const done = t.status === "done";
          return (
            <Card key={t.task_id} className={`p-4 border-slate-200 shadow-none row-enter ${done ? "opacity-60" : ""}`} data-testid={`task-row-${t.task_id}`}>
              <div className="flex items-start gap-3">
                <Checkbox checked={done} onCheckedChange={() => toggleDone(t)} className="mt-1" data-testid={`task-done-${t.task_id}`} />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className={`font-medium ${done ? "line-through text-slate-500" : "text-slate-900"}`}>{t.title}</div>
                    <Badge variant="outline" className={PRIO_TONE[t.priority]}>{t.priority}</Badge>
                    {remindDue && <Badge className="bg-amber-500 hover:bg-amber-500 text-white"><Bell className="w-3 h-3 mr-1" /> Reminder</Badge>}
                    {overdue && !done && <Badge className="bg-rose-600 hover:bg-rose-600">Overdue</Badge>}
                  </div>
                  {t.description && <div className="text-xs text-slate-600 mt-1 whitespace-pre-wrap">{t.description}</div>}
                  <div className="text-[11px] font-mono-plex text-slate-500 mt-2 flex gap-3 flex-wrap">
                    <span>Due {new Date(t.due_at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}</span>
                    {t.reminder_at && <span className="text-amber-700">⏰ {new Date(t.reminder_at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}</span>}
                    {t.assigned_to_name && <span>· {t.assigned_to_name}</span>}
                    {t.facility_name && <span>· {t.facility_name}</span>}
                    {t.repeat && t.repeat !== "none" && <span className="flex items-center gap-0.5"><Repeat className="w-3 h-3" /> {t.repeat}</span>}
                  </div>
                </div>
                <div className="flex gap-1">
                  {remindDue && !done && (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button size="sm" variant="outline" data-testid={`task-snooze-${t.task_id}`}><Clock className="w-3.5 h-3.5 mr-1" /> Snooze</Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => snooze(t.task_id, 15)}>15 minutes</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => snooze(t.task_id, 60)}>1 hour</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => snooze(t.task_id, 60 * 4)}>4 hours</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => snooze(t.task_id, 60 * 24)}>Tomorrow</DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                  <Button size="sm" variant="ghost" onClick={() => edit(t)}>Edit</Button>
                  <Button size="icon" variant="ghost" onClick={() => del(t.task_id)}><Trash2 className="w-4 h-4 text-rose-500" /></Button>
                </div>
              </div>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
